import type { TextStyle } from 'pixi.js';
import { getLocalizedText } from '@softgames/localization';
import type { DioramaState } from '../../../app/state/diorama/dioramaState';
import { dioramaConfigs } from '../../../app/state/diorama/DioramaConfig';
import { DioramaProgressBarView } from './DioramaProgressBarView';
import { DioramaChestView } from './DioramaChestView';
import { CurvedText } from '../common/text/CurvedText';
import type { DioramaHeaderLayout, DioramaProgressBarLayout } from '../../../libs/layouts/types/LayoutShapes';

export type DioramaHeaderExportLayout = DioramaHeaderLayout & DioramaProgressBarLayout;

export class DioramaHeaderView {
  private readonly dioramaProgressBarView: DioramaProgressBarView;

  private readonly dioramaChestView: DioramaChestView;

  private islandText?: CurvedText;

  private dioramaText?: CurvedText;

  constructor(
    private readonly layout: DioramaHeaderExportLayout,
    dioramaState: DioramaState,
  ) {
    this.dioramaProgressBarView = new DioramaProgressBarView(layout, dioramaState, true);

    this.dioramaChestView = new DioramaChestView(layout.chestSlot);
    this.dioramaChestView.onClicked.connect(() => {
      this.dioramaChestView.toggleRewardBubble(true);
    });
  }

  public updateView(dioramaIndex: number, isCompleted: boolean): void {
    const dioramaConfig = dioramaConfigs[dioramaIndex];

    this.showIslandText(dioramaIndex);
    this.showDioramaName(dioramaConfig.name);

    this.dioramaProgressBarView.setProgress(dioramaIndex);

    this.dioramaChestView.setDioramaChestConfig({
      reward: dioramaConfig.reward,
      isCompleted,
    });
  }

  public addPiecesToProgressBar(additionalPieces: number): void {
    this.dioramaProgressBarView.addTilesToProgress(additionalPieces);
  }

  public hide(): void {
    this.layout.root.alpha = 0;
  }

  public fadeIn(): void {
    this.layout.root.tweenTo({ alpha: 1 }, 250, 'quadOut', true);
  }

  public fadeOut(): void {
    this.layout.root.tweenTo({ alpha: 0 }, 250, 'quadOut', true);
  }

  private showDioramaName(dioramaName: string): void {
    if (this.dioramaText) {
      this.dioramaText.destroy();
    }

    const textStyle = {
      fontSize: 50,
      fontFamily: 'Baloo',
      fill: 'white',
      padding: 0,
      stroke: {
        color: 0x8d2400,
        width: 3,
      },
      align: 'left',
      dropShadow: {
        alpha: 1,
        angle: 0,
        color: 0x4c0501,
        distance: 2,
        blur: 0,
      },
      letterSpacing: 0,
    } as TextStyle;

    this.dioramaText = new CurvedText({
      textStyle,
      text: getLocalizedText(dioramaName),
      radius: 1100,
    }).props({
      parent: this.layout.ribbon,
      y: -10,
    });
  }

  private showIslandText(dioramaIndex: number): void {
    if (this.islandText) {
      this.islandText.destroy();
    }

    const textStyle = {
      fontSize: 40,
      fontFamily: 'Baloo',
      fill: 'white',
      padding: 0,
      stroke: {
        color: 0x8d2400,
        width: 3,
      },
      align: 'left',
      dropShadow: {
        alpha: 1,
        angle: 0,
        color: 0x4c0501,
        distance: 2,
        blur: 0,
      },
      letterSpacing: 0,
    } as TextStyle;

    this.islandText = new CurvedText({
      textStyle,
      text: getLocalizedText('Island <nr>').replace('<nr>', `${dioramaIndex + 1}`),
      radius: 1500,
    }).props({
      parent: this.layout.ribbon,
      y: -52,
    });
  }
}
